import {
  AnnouncementCard,
  AnnouncementData,
  AnnouncementDetailData,
  AnnouncementMetrics,
  AnnouncementStatus,
  CreateAnnouncementCardData,
  UpdateAnnouncementCardData,
} from '@/api/announcements/types';

export const USE_MOCK = false;

const ELIGIBLE_USERS = 1250;

let nextAnnouncementId = 100;
let nextCardId = 1000;

interface MockAnnouncement {
  id: number;
  title: string;
  status: AnnouncementStatus;
  created_at: string;
  updated_at: string;
  published_at: string | null;
  deactivated_at: string | null;
  created_by: number;
  updated_by: number;
  viewed: number;
  completed: number;
  read_later: number;
  dismissed: number;
  cards: AnnouncementCard[];
}

function buildMetrics(item: MockAnnouncement): AnnouncementMetrics {
  const notViewed = ELIGIBLE_USERS - item.viewed - item.read_later;
  return {
    eligible: ELIGIBLE_USERS,
    viewed: item.viewed,
    completed: item.completed,
    read_later: item.read_later,
    dismissed: item.dismissed,
    not_viewed: Math.max(notViewed, 0),
  };
}

function buildCard(
  announcementId: number,
  order: number,
  richTitle: string,
  richText: string,
  videoUrl: string | null
): AnnouncementCard {
  nextCardId += 1;
  return {
    id: nextCardId,
    announcement_id: announcementId,
    order,
    rich_title: richTitle,
    rich_text: richText,
    video_url: videoUrl,
    image_path: null,
    image_url: null,
  };
}

const defaultCards = [
  {
    richTitle: 'Nova funcionalidade de laudos',
    richText:
      '<p>Agora você pode gerar laudos com ' +
      '<strong>IA em poucos segundos</strong>. ' +
      'Economize tempo e aumente sua produtividade. ' +
      '<a href="https://proto.com.br/laudos" ' +
      'target="_blank" rel="noopener noreferrer">Saiba mais</a>.</p>',
    videoUrl: 'https://youtube.com/@proto.oficial/videos',
  },
  {
    richTitle: 'Integração com mapas melhorada',
    richText:
      '<p>Visualize todas as suas amostras no mapa com:</p>' +
      '<ul><li>Filtros avançados</li><li>Marcações personalizadas</li></ul>',
    videoUrl: null,
  },
  {
    richTitle: 'Relatórios mais completos',
    richText:
      '<p>Os relatórios agora incluem <em>gráficos comparativos</em> e ' +
      'análise de tendências do mercado.</p>',
    videoUrl: null,
  },
];

function buildAnnouncement(
  data: Omit<MockAnnouncement, 'cards'>,
  cardsCount: number
): MockAnnouncement {
  const cards = defaultCards
    .slice(0, cardsCount)
    .map((card, index) =>
      buildCard(
        data.id,
        index + 1,
        card.richTitle,
        card.richText,
        card.videoUrl
      )
    );
  return { ...data, cards };
}

const store: MockAnnouncement[] = [
  buildAnnouncement(
    {
      id: 1,
      title: 'Novidades da versão 2.5',
      status: 'active',
      created_at: '2026-01-04T10:00:00Z',
      updated_at: '2026-01-04T14:00:00Z',
      published_at: '2026-01-04T14:00:00Z',
      deactivated_at: null,
      created_by: 1,
      updated_by: 1,
      viewed: 847,
      completed: 623,
      read_later: 156,
      dismissed: 68,
    },
    3
  ),
  buildAnnouncement(
    {
      id: 2,
      title: 'Atualização de segurança',
      status: 'draft',
      created_at: '2026-06-28T09:30:00Z',
      updated_at: '2026-06-28T09:30:00Z',
      published_at: null,
      deactivated_at: null,
      created_by: 1,
      updated_by: 1,
      viewed: 0,
      completed: 0,
      read_later: 0,
      dismissed: 0,
    },
    2
  ),
  buildAnnouncement(
    {
      id: 3,
      title: 'Novo fluxo de vistorias',
      status: 'draft',
      created_at: '2026-06-15T16:20:00Z',
      updated_at: '2026-06-15T16:20:00Z',
      published_at: null,
      deactivated_at: null,
      created_by: 1,
      updated_by: 1,
      viewed: 0,
      completed: 0,
      read_later: 0,
      dismissed: 0,
    },
    3
  ),
  buildAnnouncement(
    {
      id: 5,
      title: 'Novidades da versão 2.4',
      status: 'inactive',
      created_at: '2025-11-20T10:00:00Z',
      updated_at: '2025-11-22T09:00:00Z',
      published_at: '2025-11-22T09:00:00Z',
      deactivated_at: '2026-01-04T14:00:00Z',
      created_by: 1,
      updated_by: 1,
      viewed: 912,
      completed: 704,
      read_later: 120,
      dismissed: 88,
    },
    3
  ),
  buildAnnouncement(
    {
      id: 6,
      title: 'Boas vindas 2026',
      status: 'inactive',
      created_at: '2025-12-28T08:00:00Z',
      updated_at: '2026-01-02T08:00:00Z',
      published_at: '2026-01-02T08:00:00Z',
      deactivated_at: '2026-01-04T10:00:00Z',
      created_by: 1,
      updated_by: 1,
      viewed: 1043,
      completed: 987,
      read_later: 40,
      dismissed: 16,
    },
    1
  ),
];

function toAnnouncementData(item: MockAnnouncement): AnnouncementData {
  return {
    id: item.id,
    title: item.title,
    status: item.status,
    cards_count: item.cards.length,
    created_at: item.created_at,
    published_at: item.published_at,
    viewed: item.viewed,
    completed: item.completed,
  };
}

function toDetailData(item: MockAnnouncement): AnnouncementDetailData {
  return {
    id: item.id,
    title: item.title,
    status: item.status,
    published_at: item.published_at,
    deactivated_at: item.deactivated_at,
    created_at: item.created_at,
    updated_at: item.updated_at,
    created_by: item.created_by,
    updated_by: item.updated_by,
    cards: [...item.cards].sort((a, b) => a.order - b.order),
    metrics: buildMetrics(item),
  };
}

function deactivateCurrentActive(): void {
  const active = store.find((item) => item.status === 'active');
  if (active) {
    active.status = 'inactive';
    active.deactivated_at = new Date().toISOString();
  }
}

function findAnnouncementByCardId(
  cardId: number
): MockAnnouncement | undefined {
  return store.find((item) => item.cards.some((card) => card.id === cardId));
}

export function listMockAnnouncements(): AnnouncementData[] {
  return store.map(toAnnouncementData);
}

export function getMockAnnouncement(
  id: number
): AnnouncementDetailData | undefined {
  const found = store.find((item) => item.id === id);
  return found ? toDetailData(found) : undefined;
}

export function getActiveMockAnnouncement(): AnnouncementData | undefined {
  const found = store.find((item) => item.status === 'active');
  return found ? toAnnouncementData(found) : undefined;
}

export function createMockAnnouncement(title: string): { id: number } {
  nextAnnouncementId += 1;
  const now = new Date().toISOString();
  store.push({
    id: nextAnnouncementId,
    title,
    status: 'draft',
    created_at: now,
    updated_at: now,
    published_at: null,
    deactivated_at: null,
    created_by: 1,
    updated_by: 1,
    viewed: 0,
    completed: 0,
    read_later: 0,
    dismissed: 0,
    cards: [],
  });
  return { id: nextAnnouncementId };
}

export function updateMockAnnouncementTitle(id: number, title: string): void {
  const found = store.find((item) => item.id === id);
  if (!found) return;
  found.title = title;
  found.updated_at = new Date().toISOString();
}

export function updateMockAnnouncementStatus(
  id: number,
  status: AnnouncementStatus
): void {
  const found = store.find((item) => item.id === id);
  if (!found) return;

  if (status === 'active') {
    deactivateCurrentActive();
    if (!found.published_at) found.published_at = new Date().toISOString();
  } else {
    found.deactivated_at = new Date().toISOString();
  }

  found.status = status;
  found.updated_at = new Date().toISOString();
}

export function deleteMockAnnouncement(id: number): void {
  const index = store.findIndex((item) => item.id === id);
  if (index >= 0) store.splice(index, 1);
}

export function createMockAnnouncementCard(
  announcementId: number,
  data: CreateAnnouncementCardData
): { id: number } {
  const found = store.find((item) => item.id === announcementId);
  if (!found) return { id: 0 };

  nextCardId += 1;
  const imageUrl = data.image ? URL.createObjectURL(data.image) : null;

  found.cards.push({
    id: nextCardId,
    announcement_id: announcementId,
    order: found.cards.length + 1,
    rich_title: data.richTitle,
    rich_text: data.richText,
    video_url: imageUrl ? null : data.videoUrl ?? null,
    image_path: imageUrl ? `mock/${nextCardId}` : null,
    image_url: imageUrl,
  });

  return { id: nextCardId };
}

export function updateMockAnnouncementCard(
  cardId: number,
  data: UpdateAnnouncementCardData
): void {
  const announcement = findAnnouncementByCardId(cardId);
  const card = announcement?.cards.find((item) => item.id === cardId);
  if (!card) return;

  if (data.richTitle !== undefined) card.rich_title = data.richTitle;
  if (data.richText !== undefined) card.rich_text = data.richText;

  if (data.image) {
    card.image_url = URL.createObjectURL(data.image);
    card.image_path = `mock/${cardId}`;
    card.video_url = null;
  } else {
    if (data.videoUrl !== undefined) card.video_url = data.videoUrl || null;
    if (data.removeImage) {
      card.image_url = null;
      card.image_path = null;
    }
  }
}

export function deleteMockAnnouncementCard(cardId: number): void {
  const announcement = findAnnouncementByCardId(cardId);
  if (!announcement) return;
  announcement.cards = announcement.cards
    .filter((card) => card.id !== cardId)
    .map((card, index) => ({ ...card, order: index + 1 }));
}

export function reorderMockAnnouncementCards(
  announcementId: number,
  cardIds: number[]
): void {
  const found = store.find((item) => item.id === announcementId);
  if (!found) return;
  const byId = new Map(found.cards.map((card) => [card.id, card]));
  cardIds.forEach((cardId, index) => {
    const card = byId.get(cardId);
    if (card) card.order = index + 1;
  });
}
